// Dependencies
import { createSlice, createAction } from '@reduxjs/toolkit';
import { PayloadAction } from '@reduxjs/toolkit/dist/createAction';
import { applyColumnResize, ColumnResizePayload } from '../../utils/list/utils/columnResize';
// Types
import { Column } from '../objects/column';
import { Roadbook } from '../objects/roadbook';
import { Picture } from '../../picture/objects/picture';
import { RoadbookState } from '../objects/roadbookState';
import { Preferences } from '../../profile/objects/preferences';
import { RoadbookCategory } from '../../roadbookCategory/objects/roadbookCategory';
import { RoadbookRubric } from '../../roadbookDefaultRubric/objects/roadbookRubric';
import { RoadbookPictureType } from '../../roadbookPictureType/objects/roadbookPictureType';

const setPicture = createAction<boolean | Picture>('PICTURE_SET_PICTURE');
const setPreferences = createAction<Preferences[]>('PROFILE_SET_PREFERENCES');
const addCustomPictures = createAction<Picture[]>('PICTURE_ADD_CUSTOM_PICTURES');

const initialState: RoadbookState = {
    category_delete: null,
    category_index: 0,
    category_open: false,
    columns: [
        {
            label: 'destination',
            field_name: 'DESTINATION',
            checked: true,
            order_by: 'destination__current_version__localization__name',
            fixed_width: false,
            last_updated: false,
            width: null
        },
        {
            label: 'created-date',
            field_name: 'CREATED_DATE',
            checked: false,
            order_by: 'created_date',
            fixed_width: true,
            last_updated: false,
            width: null
        },
        {
            label: 'created-by',
            field_name: 'CREATED_BY',
            checked: false,
            order_by: 'creator__username',
            fixed_width: true,
            last_updated: false,
            width: null
        },
        {
            label: 'modified-date',
            field_name: 'MODIFIED_DATE',
            checked: true,
            order_by: 'modified_date',
            fixed_width: true,
            last_updated: false,
            width: null
        },
        {
            label: 'modified-by',
            field_name: 'MODIFIED_BY',
            checked: true,
            order_by: 'last_author__username',
            fixed_width: true,
            last_updated: false,
            width: null
        },
        {
            label: 'activated',
            field_name: 'ACTIVATED',
            checked: true,
            order_by: 'activated',
            fixed_width: true,
            last_updated: false,
            width: null
        }
    ],
    count: 0,
    create: false,
    current_category_index: null,
    destination: null,
    destination_error: '',
    duplicate_roadbook: null,
    duplicate_roadbook_target_destination: null,
    duplicate_roadbook_target_destination_error: '',
    edit: null,
    editor_pictures: null,
    export_full: false,
    export_list: false,
    export_type: 'CSV',
    filter_created_date_gte: null,
    filter_created_date_lte: null,
    filter_creator: '',
    filter_last_author: '',
    filter_modified_date_gte: null,
    filter_modified_date_lte: null,
    filter_open: false,
    filter_period: 'CREATION',
    filter_period_type: 0,
    limit: 50,
    list: null,
    order: 'asc',
    order_by: 'name',
    page: 0,
    pictures: [],
    picture_category_index: null,
    picture_rubric_index: null,
    preferences: [],
    refresh: false,
    roadbook: null,
    roadbook_data: [],
    roadbook_picture_types: [],
    rubric_delete: null,
    rubric_index: 0,
    rubric_open: false,
    running: false,
    search: '',
    selected_category: null,
    selected_default_rubric: null,
};

export const roadbookSlice = createSlice({
    name: 'roadbook',
    initialState,
    reducers: {
        addCategory: (state) => {
            if (state.selected_category) {
                const roadbook_data = [...state.roadbook_data];
                roadbook_data.push({
                    ...state.selected_category,
                    rubrics: []
                });
                state.roadbook_data = roadbook_data;
                state.selected_category = null;
                state.category_open = false;
            }
        },
        addPreferences: (state, action) => {
            state.preferences = [...state.preferences, action.payload];
        },
        addDefaultRubric: (state) => {
            if (state.current_category_index !== null && state.selected_default_rubric !== null) {
                const roadbook_data = [...state.roadbook_data];
                const rubrics = [...roadbook_data[state.current_category_index].rubrics];
                rubrics.push(state.selected_default_rubric);
                roadbook_data[state.current_category_index].rubrics = rubrics;
                state.roadbook_data = roadbook_data;
            }
            state.rubric_open = false;
            state.current_category_index = null;
        },
        addRubric: (state, action: PayloadAction<number>) => {
            const roadbook_data = [...state.roadbook_data];
            const rubrics = [...roadbook_data[action.payload].rubrics];
            rubrics.push({
                created_date: new Date(),
                creator: null,
                default: false,
                id: -1 * Math.random(),
                last_author: null,
                localization: [{
                    title: '',
                    description: '',
                    locale: 1,
                }],
                modified_date: new Date()
            });
            roadbook_data[action.payload].rubrics = rubrics;
            state.roadbook_data = roadbook_data;
        },
        deleteCategory: (state) => {
            const roadbook_data = [...state.roadbook_data];
            roadbook_data.splice(state.category_index, 1);
            state.roadbook_data = roadbook_data;
            state.category_delete = null;
            state.category_index = 0;
        },
        deletePicture: (state, action) => {
            const pictures = [...state.pictures];
            pictures.splice(action.payload, 1);
            state.pictures = pictures;
        },
        deleteRubric: (state) => {
            const roadbook_data = [...state.roadbook_data];
            const rubrics = [...roadbook_data[state.category_index].rubrics];
            rubrics.splice(state.rubric_index, 1);
            roadbook_data[state.category_index].rubrics = rubrics;
            state.roadbook_data = roadbook_data;
            state.rubric_delete = null;
            state.category_index = 0;
            state.rubric_index = 0;
        },
        duplicateRoadbook: (state, action) => {
            if (action.payload === false) {
                state.duplicate_roadbook = null;
                state.duplicate_roadbook_target_destination = null;
            } else {
                state.duplicate_roadbook = action.payload;
                state.duplicate_roadbook_target_destination = null;
            }
        },
        duplicateRoadbookErrors: (state, action) => {
            state.duplicate_roadbook_target_destination_error = action.payload.join(',');
        },
        moveDownColumn: (state, action) => {
            const columns = [...state.columns];
            for (let i = 0; i < columns.length; i++) {
                columns[i].last_updated = false;
            }
            const column = { ...columns[action.payload + 1] };
            columns[action.payload + 1] = columns[action.payload];
            columns[action.payload + 1].last_updated = true;
            columns[action.payload] = column;
            state.columns = columns;
        },
        moveUpColumn: (state, action) => {
            const columns = [...state.columns];
            for (let i = 0; i < columns.length; i++) {
                columns[i].last_updated = false;
            }
            const column = { ...columns[action.payload - 1] };
            columns[action.payload - 1] = columns[action.payload];
            columns[action.payload - 1].last_updated = true;
            columns[action.payload] = column;
            state.columns = columns;
        },
        resetFilters: (state) => {
            state.filter_created_date_gte = null;
            state.filter_created_date_lte = null;
            state.filter_creator = '';
            state.filter_last_author = '';
            state.filter_modified_date_gte = null;
            state.filter_modified_date_lte = null;
            state.filter_period = 'CREATION';
            state.filter_period_type = 0;
        },
        resetRoadbook: (state) => {
            state.category_delete = null;
            state.category_index = 0;
            state.category_open = false;
            state.create = false;
            state.current_category_index = null;
            state.destination = null;
            state.destination_error = '';
            state.duplicate_roadbook = null;
            state.duplicate_roadbook_target_destination = null;
            state.duplicate_roadbook_target_destination_error = '';
            state.edit = null;
            state.editor_pictures = null;
            state.pictures = [];
            state.picture_rubric_index = null;
            state.picture_category_index = null;
            state.refresh = !state.refresh;
            state.roadbook = null;
            state.roadbook_data = [];
            state.roadbook_picture_types = [];
            state.rubric_delete = null;
            state.rubric_index = 0;
            state.rubric_open = false;
            state.selected_category = null;
            state.selected_default_rubric = null;
        },
        setEdit: (state, action) => {
            if (state.roadbook) {
                const destination = state.roadbook.destination;
                state.destination = {
                    id: destination.id,
                    international_name: destination.data.international_name,
                    localization: destination.data.localization,
                };
                const categories = [];
                state.roadbook.categories.map(category => categories.push({ ...category.category, rubrics: category.rubrics }));
                state.roadbook_data = [...categories];
                state.pictures = state.roadbook.cover_pictures.map(picture => {
                    return {
                        start_date: picture.start_date !== null ? new Date(picture.start_date) : null,
                        end_date: picture.end_date !== null ? new Date(picture.end_date) : null,
                        picture: picture.picture,
                        type: picture.type
                    };
                });
            }
            state.edit = action.payload;
        },
        setExportType: (state, action) => {
            state.export_type = action.payload;
        },
        setLimit: (state, action) => {
            state.list = null;
            state.limit = action.payload;
        },
        setList: (state, action) => {
            state.count = action.payload.count;
            state.list = action.payload.results;
            state.running = false;
        },
        setOrderBy: (state, action) => {
            state.list = null;
            state.order_by = action.payload;
            state.order = state.order_by === action.payload && state.order === 'asc' ? 'desc' : 'asc';
        },
        setPage: (state, action) => {
            state.list = null;
            state.page = action.payload;
        },
        setRoadbook: (state, action: PayloadAction<Roadbook>) => {
            state.edit = null;
            state.roadbook = action.payload;
        },
        setRoadbookPictureTypes: (state, action: PayloadAction<RoadbookPictureType[]>) => {
            state.roadbook_picture_types = action.payload;
        },
        setRoadbookPreferences: (state, action) => {
            state.preferences = action.payload;
        },
        setRunning: (state, action) => {
            state.running = action.payload;
        },
        setSearch: (state, action) => {
            state.list = null;
            state.page = 0;
            state.search = action.payload;
        },
        toggleCategoryDelete: (state, action) => {
            state.category_delete = action.payload.category;
            state.category_index = action.payload.index;
        },
        toggleCategoryOpen: (state) => {
            state.category_open = !state.category_open;
        },
        toggleColumn: (state, action) => {
            const columns = [...state.columns];
            const column = { ...columns[action.payload] };
            column.checked = !column.checked;
            columns[action.payload] = column;
            state.columns = columns;
        },
        toggleCreate: (state) => {
            state.create = !state.create;
        },
        toggleExport: (state, action) => {
            state.export_list = action.payload;
        },
        toggleExportFull: (state) => {
            state.export_full = !state.export_full;
        },
        toggleFilters: (state) => {
            state.filter_open = !state.filter_open;
        },
        toggleRefresh: (state) => {
            state.refresh = !state.refresh;
        },
        toggleRubricDelete: (state, action) => {
            state.rubric_delete = action.payload.rubric;
            state.category_index = action.payload.category_index;
            state.rubric_index = action.payload.rubric_index;
        },
        toggleRubricOpen: (state, action: PayloadAction<number>) => {
            state.rubric_open = !state.rubric_open;
            state.current_category_index = action.payload;
        },
        updateDestination: (state, action) => {
            state.destination = action.payload;
        },
        updateFilterCreatedDateGte: (state, action) => {
            state.filter_created_date_gte = action.payload;
        },
        updateFilterCreator: (state, action) => {
            state.filter_created_date_lte = action.payload;
        },
        updateFilterCreatedDateLte: (state, action) => {
            state.filter_creator = action.payload;
        },
        updateFilterLastAuthor: (state, action) => {
            state.filter_last_author = action.payload;
        },
        updateFilterModifiedDateGte: (state, action) => {
            state.filter_modified_date_gte = action.payload;
        },
        updateFilterModifiedDateLte: (state, action) => {
            state.filter_modified_date_lte = action.payload;
        },
        updateFilterPeriod: (state, action) => {
            state.filter_period = action.payload;
            state.filter_period_type = 0;
            state.filter_created_date_gte = action.payload === 'MODIFICATION' ? null : state.filter_created_date_gte;
            state.filter_created_date_lte = action.payload === 'MODIFICATION' ? null : state.filter_created_date_lte;
            state.filter_modified_date_gte = action.payload === 'CREATION' ? null : state.filter_modified_date_gte;
            state.filter_modified_date_lte = action.payload === 'CREATION' ? null : state.filter_modified_date_lte;
        },
        updateFilterPeriodType: (state, action) => {
            state.filter_period_type = action.payload;
        },
        updatePictureEndDate: (state, action) => {
            const { value, index } = action.payload;
            const pictures = [...state.pictures];
            const picture = { ...pictures[index] };
            picture.end_date = value;
            pictures[index] = picture;
            state.pictures = pictures;
        },
        updatePictureStartDate: (state, action) => {
            const { value, index } = action.payload;
            const pictures = [...state.pictures];
            const picture = { ...pictures[index] };
            picture.start_date = value;
            pictures[index] = picture;
            state.pictures = pictures;
        },
        updatePictureType: (state, action: PayloadAction<{value: number, index: number}>) => {
            const { value, index } = action.payload;
            const selected_picture_type = state.roadbook_picture_types.find(picture_type => picture_type.id === value);
            if (selected_picture_type) {
                const pictures = [...state.pictures];
                const picture = { ...pictures[index] };
                picture.type = selected_picture_type;
                pictures[index] = picture;
                state.pictures = pictures;
            }
        },
        updatePictureIndex: (state, action: PayloadAction<{ picture_category_index: number, picture_rubric_index: number }>) => {
            state.editor_pictures = null;
            state.picture_category_index = action.payload.picture_category_index;
            state.picture_rubric_index = action.payload.picture_rubric_index;
        },
        updateRoadbookDataCategories: (state, action: PayloadAction<(RoadbookCategory & {rubrics: RoadbookRubric[]})[]>) => {
            state.roadbook_data = action.payload;
        },
        updateRubricDescription: (state, action: PayloadAction<{category_index: number, rubric_index: number, current_locale: null | number, value: string}>) => {
            if (action.payload.current_locale) {
                const roadbook_data = [...state.roadbook_data];
                const rubrics = [...roadbook_data[action.payload.category_index].rubrics];
                const rubric = { ...rubrics[action.payload.rubric_index] };
                const rubric_localizations = [...rubric.localization];
                const rubric_localization_index = rubric_localizations.findIndex(locale => locale.locale === action.payload.current_locale);
                if (rubric_localization_index !== -1) {
                    const rubric_localization = { ...rubric_localizations[rubric_localization_index] };
                    rubric_localization.description = action.payload.value;
                    rubric_localizations[rubric_localization_index] = rubric_localization;
                } else {
                    rubric_localizations.push({
                        title: '',
                        description: action.payload.value,
                        locale: action.payload.current_locale
                    });
                }
                rubric.localization = rubric_localizations;
                rubrics[action.payload.rubric_index] = rubric;
                roadbook_data[action.payload.category_index].rubrics = rubrics;
                state.roadbook_data = roadbook_data;
            }
        },
        updateDuplicateRoadbookTargetDestination: (state, action) => {
            state.duplicate_roadbook_target_destination = action.payload;
        },
        updateListItemActivated: (state, action) => {
            if (state.list !== null) {
                const list = [...state.list];
                for (let i = 0; i < list.length; i++) {
                    if (list[i].id === action.payload.id) {
                        list[i] = { ...list[i], activated: action.payload.activated };
                        break;
                    }
                }
                state.list = list;
            }
        },
        updateRubricTitle: (state, action: PayloadAction<{category_index: number, rubric_index: number, current_locale: null | number, value: string}>) => {
            if (action.payload.current_locale) {
                const roadbook_data = [...state.roadbook_data];
                const rubrics = [...roadbook_data[action.payload.category_index].rubrics];
                const rubric = { ...rubrics[action.payload.rubric_index] };
                const rubric_localizations = [...rubric.localization];
                const rubric_localization_index = rubric_localizations.findIndex(locale => locale.locale === action.payload.current_locale);
                if (rubric_localization_index !== -1) {
                    const rubric_localization = { ...rubric_localizations[rubric_localization_index] };
                    rubric_localization.title = action.payload.value;
                    rubric_localizations[rubric_localization_index] = rubric_localization;
                } else {
                    rubric_localizations.push({
                        title: action.payload.value,
                        description: '',
                        locale: action.payload.current_locale
                    });
                }
                rubric.localization = rubric_localizations;
                rubrics[action.payload.rubric_index] = rubric;
                roadbook_data[action.payload.category_index].rubrics = rubrics;
                state.roadbook_data = roadbook_data;
            }
        },
        updateSelectedCategory: (state, action) => {
            state.selected_category = action.payload;
        },
        updateSelectedDefaultRubric: (state, action) => {
            state.selected_default_rubric = action.payload;
        },
        setColumns: (state, action: PayloadAction<Column[]>) => {
            state.columns = action.payload;
        },
        resizeColumn: (state, action: PayloadAction<ColumnResizePayload>) => {
            state.columns = applyColumnResize(state.columns, action.payload);
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(setPreferences, (state, action) => {
                const field_name: string[] = [];
                const columns: Column[] = [];
                const preferences: Preferences[] = [];
                let order_by = '-created_date';
                let order: 'asc' | 'desc' = 'asc';
                action.payload.map((preference: Preferences) => {
                    if (preference.module === 'ROADBOOK') {
                        if (!field_name.includes(preference.field_name)) {
                            switch (preference.field_name) {
                                case 'DESTINATION':
                                    columns.push({
                                        label: 'destination',
                                        field_name: preference.field_name,
                                        checked: preference.active,
                                        order_by: 'destination__current_version__localization__name',
                                        fixed_width: false,
                                        last_updated: false,
                                        width: null
                                    });
                                    order_by = preference.ordering_asc !== null ? 'localization__name' : order_by;
                                    break;
                                case 'CREATED_DATE':
                                    columns.push({
                                        label: 'created-date',
                                        field_name: preference.field_name,
                                        checked: preference.active,
                                        order_by: 'created_date',
                                        fixed_width: true,
                                        last_updated: false,
                                        width: null
                                    });
                                    order_by = preference.ordering_asc !== null ? 'created_date' : order_by;
                                    break;
                                case 'CREATED_BY':
                                    columns.push({
                                        label: 'created-by',
                                        field_name: preference.field_name,
                                        checked: preference.active,
                                        order_by: 'creator__username',
                                        fixed_width: false,
                                        last_updated: false,
                                        width: null
                                    });
                                    order_by = preference.ordering_asc !== null ? 'creator__username' : order_by;
                                    break;
                                case 'MODIFIED_DATE':
                                    columns.push({
                                        label: 'modified-date',
                                        field_name: preference.field_name,
                                        checked: preference.active,
                                        order_by: 'modified_date',
                                        fixed_width: true,
                                        last_updated: false,
                                        width: null
                                    });
                                    order_by = preference.ordering_asc !== null ? 'modified_date' : order_by;
                                    break;
                                case 'MODIFIED_BY':
                                    columns.push({
                                        label: 'modified-by',
                                        field_name: preference.field_name,
                                        checked: preference.active,
                                        order_by: 'last_author__username',
                                        fixed_width: false,
                                        last_updated: false,
                                        width: null
                                    });
                                    order_by = preference.ordering_asc !== null ? 'last_author__username' : order_by;
                                    break;
                                case 'ACTIVATED':
                                    columns.push({
                                        label: 'activated',
                                        field_name: preference.field_name,
                                        checked: preference.active,
                                        order_by: 'activated',
                                        fixed_width: false,
                                        last_updated: false,
                                        width: null
                                    });
                                    order_by = preference.ordering_asc !== null ? 'activated' : order_by;
                                    break;
                                default:
                                    break;
                            }
                            preferences.push(preference);
                            field_name.push(preference.field_name);
                            order = preference.ordering_asc !== null ? preference.ordering_asc ? 'asc' : 'desc' : order;
                        }
                    }
                });
                if (columns.length !== state.columns.length) {
                    state.columns.map((column) => {
                        let added = false;
                        for (let i = 0; i < columns.length; i++) {
                            if (columns[i].label === column.label) {
                                added = true;
                                break;
                            }
                        }
                        if (!added) {
                            columns.push({ ...column });
                        }
                    });
                }
                state.preferences = preferences;
                state.columns = columns;
                state.order_by = order_by;
                state.order = order;
            })
            .addCase(addCustomPictures, (state, action) => {
                if (state.roadbook !== null || state.create) {
                    if (state.picture_category_index !== null && state.picture_rubric_index !== null) {
                        state.editor_pictures = [...action.payload];
                    } else {
                        const pictures: {start_date: null | Date, end_date: null | Date, picture: Picture, type: null | RoadbookPictureType}[] = [];
                        action.payload.map(picture => pictures.push({ start_date: null, end_date: null, picture: { ...picture }, type: null }));
                        state.pictures = [...state.pictures, ...pictures];
                    }
                }
            })
            .addCase(setPicture, (state, action) => {
                if (action.payload !== true && action.payload !== false) {
                    const pictures = [...state.pictures];
                    for (let i = 0; i < pictures.length; i++) {
                        if (pictures[i].id === action.payload.id) {
                            pictures[i] = { ...action.payload };
                            break;
                        }
                    }
                    state.pictures = pictures;
                }
            })
            .addDefaultCase(() => {});
    }
});

export const {
    addCategory,
    addPreferences,
    addDefaultRubric,
    addRubric,
    deleteCategory,
    deletePicture,
    deleteRubric,
    duplicateRoadbook,
    duplicateRoadbookErrors,
    moveDownColumn,
    moveUpColumn,
    resetFilters,
    resetRoadbook,
    setEdit,
    setExportType,
    setLimit,
    setList,
    setOrderBy,
    setPage,
    setRoadbook,
    setRoadbookPictureTypes,
    setRoadbookPreferences,
    setRunning,
    setSearch,
    toggleCategoryDelete,
    toggleCategoryOpen,
    toggleColumn,
    toggleCreate,
    toggleExport,
    toggleExportFull,
    toggleFilters,
    toggleRefresh,
    toggleRubricDelete,
    toggleRubricOpen,
    updateDestination,
    updateFilterCreatedDateGte,
    updateFilterCreator,
    updateFilterCreatedDateLte,
    updateFilterLastAuthor,
    updateFilterModifiedDateGte,
    updateFilterModifiedDateLte,
    updateFilterPeriod,
    updateFilterPeriodType,
    updatePictureEndDate,
    updatePictureStartDate,
    updatePictureType,
    updatePictureIndex,
    updateRoadbookDataCategories,
    updateRubricDescription,
    updateDuplicateRoadbookTargetDestination,
    updateListItemActivated,
    updateRubricTitle,
    updateSelectedCategory,
    updateSelectedDefaultRubric,
    setColumns,
    resizeColumn
} = roadbookSlice.actions;

export default roadbookSlice.reducer;